import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { StripedWordmark, TricolorStrip } from '../components/BSCAuthLayout';
import AdminUsers from '../components/AdminUsers';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { LogOut, Building2, Users, Image as ImageIcon, Save, Trash2, Home as HomeIcon, ScrollText, Search, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
        <h1 className="text-2xl tracking-widest uppercase">Admin only</h1>
        <p className="text-white/60 text-sm tracking-widest uppercase">You don't have permission to access this app.</p>
        <Link to="/" className="text-white/80 underline">Back to launcher</Link>
      </div>
    );
  }

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F2ED', fontFamily: 'Manrope, sans-serif' }} data-testid="admin-app-page">
      <div className="border-b relative" style={{ backgroundColor: '#0A0A0A', borderColor: '#1f1f1f' }}>
        <TricolorStrip className="absolute top-0 left-0 right-0" />
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/bsc-logo.jpeg" alt="BSC" className="w-10 h-10 rounded-lg" draggable={false} />
            <div className="flex flex-col leading-tight">
              <div className="scale-75 origin-left -my-1">
                <StripedWordmark text="ADMIN" size="sm" />
              </div>
              <span className="text-[9px] tracking-[0.35em] uppercase text-white/40 mt-0.5">BSC · Control Centre</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-white/60 hover:text-white text-xs tracking-widest uppercase flex items-center gap-1" data-testid="admin-home-link">
              <HomeIcon size={14} /> Launcher
            </Link>
            <span className="text-sm text-white/70 hidden sm:inline">{user?.name}</span>
            <Button onClick={handleLogout} variant="outline" size="sm" data-testid="admin-logout-btn"
              className="bg-transparent border-white/20 text-white/80 hover:bg-white/10 hover:text-white">
              <LogOut size={16} className="mr-2" />Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="company">
          <TabsList style={{ backgroundColor: '#F2EFEB' }}>
            <TabsTrigger value="company" data-testid="admin-tab-company"><Building2 size={14} className="mr-2" />Company</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users"><Users size={14} className="mr-2" />Users</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit"><ScrollText size={14} className="mr-2" />Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="company">
            <CompanyPanel />
          </TabsContent>

          <TabsContent value="users">
            <div className="mt-6 bg-white rounded-xl border p-6" style={{ borderColor: '#E8E3DB' }}>
              <AdminUsers currentUser={user} />
            </div>
          </TabsContent>

          <TabsContent value="audit">
            <AuditLogPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function CompanyPanel() {
  const [name, setName] = useState('');
  const [logo, setLogo] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/company`, { withCredentials: true });
      setName(r.data.name || '');
      setLogo(r.data.logo || '');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onLogoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error('Image too large. Max 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/company`, { name, logo: logo || null }, { withCredentials: true });
      toast.success('Company settings saved');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const removeLogo = () => setLogo('');

  if (loading) {
    return <div className="mt-6 p-8 text-center text-black/40">Loading…</div>;
  }

  return (
    <div className="mt-6 bg-white rounded-xl border p-6 max-w-2xl" style={{ borderColor: '#E8E3DB' }} data-testid="company-panel">
      <h3 className="text-sm tracking-widest uppercase text-black/70 mb-4 flex items-center gap-2">
        <Building2 size={16} /> Company profile
      </h3>
      <p className="text-sm text-black/50 mb-6">This name and logo appear in headers, exports and the launcher across all apps.</p>

      <div className="space-y-5">
        <div>
          <Label className="text-[10px] uppercase tracking-widest">Company name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="company-name-input"
            placeholder="e.g. BSC Industries Pvt Ltd" />
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest">Company logo</Label>
          <div className="flex items-center gap-4 mt-2">
            {logo ? (
              <img src={logo} alt="Logo" data-testid="company-logo-preview"
                className="w-20 h-20 rounded-lg object-cover border" style={{ borderColor: '#E8E3DB' }} />
            ) : (
              <div className="w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center text-black/30"
                   style={{ borderColor: '#E8E3DB' }}>
                <ImageIcon size={24} />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={onLogoChange} className="hidden" data-testid="company-logo-input" />
                <span className="px-3 py-1.5 rounded border border-black/20 text-xs uppercase tracking-widest hover:bg-black/5">
                  Choose file
                </span>
              </label>
              {logo && (
                <Button size="sm" variant="ghost" onClick={removeLogo} data-testid="company-logo-remove">
                  <Trash2 size={12} className="mr-1" />Remove
                </Button>
              )}
            </div>
          </div>
          <p className="text-[10px] text-black/40 mt-2">PNG / JPG / SVG. Max 2 MB. Square images render best.</p>
        </div>

        <div className="pt-4 border-t" style={{ borderColor: '#E8E3DB' }}>
          <Button onClick={save} disabled={saving} data-testid="company-save-btn"
            style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
            <Save size={14} className="mr-2" />{saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}


function AuditLogPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [confirmingClear, setConfirmingClear] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/audit-logs`, { withCredentials: true });
      setLogs(r.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load audit log'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = logs.filter((l) => {
    if (actionFilter && !(l.action || '').includes(actionFilter)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.user_email || '').toLowerCase().includes(q) ||
           (l.user_name || '').toLowerCase().includes(q) ||
           (l.action || '').toLowerCase().includes(q) ||
           JSON.stringify(l.details || {}).toLowerCase().includes(q);
  });

  const uniqueActions = Array.from(new Set(logs.map((l) => l.action))).sort();

  const clearAll = async () => {
    if (!confirmingClear) { setConfirmingClear(true); setTimeout(() => setConfirmingClear(false), 4000); return; }
    try {
      const r = await axios.delete(`${API}/admin/clear-logs`, { withCredentials: true });
      toast.success(r.data.message || 'Cleared');
      setConfirmingClear(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Clear failed'); }
  };

  return (
    <div className="mt-6 bg-white rounded-xl border p-6" style={{ borderColor: '#E8E3DB' }} data-testid="audit-log-panel">
      <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
        <h3 className="text-sm tracking-widest uppercase text-black/70 flex items-center gap-2">
          <ScrollText size={16} /> Audit log <span className="text-black/40 normal-case font-normal">({filtered.length} of {logs.length})</span>
        </h3>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
            <Input className="pl-9 w-64" placeholder="Search user / details" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="audit-search-input" />
          </div>
          <select className="border rounded px-2 py-1.5 text-sm bg-white" value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)} data-testid="audit-action-filter"
            style={{ borderColor: '#E8E3DB' }}>
            <option value="">All actions</option>
            {uniqueActions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh-btn">
            <RefreshCw size={12} className="mr-1" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={clearAll} data-testid="audit-clear-btn"
            style={{ borderColor: '#A43E33', color: '#A43E33' }}>
            <Trash2 size={12} className="mr-1" />{confirmingClear ? 'Click again to confirm' : 'Clear all'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-black/40">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center text-black/40">No audit log entries match.</div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-auto">
          {filtered.map((l) => (
            <div key={l.id} data-testid={`audit-log-${l.id}`}
              className="p-3 rounded border text-sm flex flex-col gap-1"
              style={{ borderColor: '#F1EDE5', backgroundColor: '#FAF7F2' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
                    {l.action}
                  </span>
                  <span className="text-black/70 font-medium">{l.user_name}</span>
                  <span className="text-black/50 text-xs">({l.user_email})</span>
                </div>
                <span className="text-[10px] tracking-widest uppercase text-black/50">
                  {l.created_at ? new Date(l.created_at).toLocaleString('en-IN') : '—'}
                </span>
              </div>
              {l.details && Object.keys(l.details).length > 0 && (
                <div className="text-xs text-black/60 ml-2">
                  {Object.entries(l.details).map(([k, v]) => (
                    <span key={k} className="mr-3">
                      <b>{k}:</b> {typeof v === 'object' ? JSON.stringify(v) : (typeof v === 'number' ? v.toFixed(2) : String(v))}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
